import { Section, Eyebrow, CTAButton } from "./Layout";
import type { ReactNode } from "react";

export type CaseStudyProps = {
  eyebrow?: string;
  title: string;
  firmType?: string;
  location?: string;
  challenge: string;
  approach: string[];
  results: { metric: string; label: string }[];
  quote?: string;
  attribution?: string;
  tone?: "light" | "muted";
  semanticSubheadings?: boolean;
  closingContent?: ReactNode;
  titleLevel?: "h2" | "h3";
  resultsCopy?: string;
  links?: ReactNode;
  contentLabels?: { challenge: string; approach: string; results: string };
  directoryOnly?: boolean;
};

export function CaseStudy({
  eyebrow = "Case study",
  title,
  firmType,
  location,
  challenge,
  approach,
  results,
  quote,
  attribution,
  tone = "muted",
  semanticSubheadings = false,
  closingContent,
  titleLevel = "h2",
  resultsCopy,
  links,
  contentLabels,
  directoryOnly = false,
}: CaseStudyProps) {
  const Title = titleLevel;
  const wrapper =
    tone === "muted" ? "bg-secondary/40 border-y border-border" : "bg-background";
  if (directoryOnly) return <div className={wrapper}><Section><p className="text-lg text-foreground leading-relaxed">See how we've grown law firms in specific markets.</p><div className="mt-6"><CTAButton to="/case-studies" variant="ghost">View case studies →</CTAButton></div></Section></div>;
  return (
    <div className={wrapper}>
      <Section>
        <Eyebrow>{eyebrow}</Eyebrow>
        <Title className="mt-4 text-4xl font-semibold text-foreground max-w-3xl">{title}</Title>
        {(firmType || location) && (
          <p className="mt-3 text-sm uppercase tracking-wider text-muted-foreground">
            {[firmType, location].filter(Boolean).join(" · ")}
          </p>
        )}
        <div className="mt-10 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div>
              {semanticSubheadings ? <h3 className="text-xs uppercase tracking-wider font-semibold text-[var(--gold-deep)]">The challenge</h3> : <div className="text-xs uppercase tracking-wider font-semibold text-[var(--gold-deep)]">{contentLabels?.challenge ?? "The challenge"}</div>}
              <p className="mt-3 text-lg text-foreground leading-relaxed">{challenge}</p>
            </div>
            <div>
              {semanticSubheadings ? <h3 className="text-xs uppercase tracking-wider font-semibold text-[var(--gold-deep)]">What we did</h3> : <div className="text-xs uppercase tracking-wider font-semibold text-[var(--gold-deep)]">{contentLabels?.approach ?? "What we did"}</div>}
              <ul className="mt-3 space-y-2">
                {approach.map((a) => (
                  <li key={a} className="flex gap-3 text-foreground">
                    <span className="text-[var(--gold-deep)] font-bold flex-shrink-0">→</span>
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
            {quote && (
              <blockquote className="rounded-xl border-l-4 border-[var(--gold)] bg-background p-6">
                <p className="text-lg italic text-foreground leading-relaxed">"{quote}"</p>
                {attribution && (
                  <footer className="mt-3 text-sm font-semibold text-muted-foreground">{attribution}</footer>
                )}
              </blockquote>
            )}
          </div>
          <aside className="rounded-xl border border-border bg-background p-6 h-fit">
            {semanticSubheadings ? <h3 className="text-xs uppercase tracking-wider font-semibold text-[var(--gold-deep)]">The results</h3> : <div className="text-xs uppercase tracking-wider font-semibold text-[var(--gold-deep)]">{contentLabels?.results ?? "The results"}</div>}
            {resultsCopy && <p className="mt-4 text-foreground leading-relaxed">{resultsCopy}</p>}
            <div className="mt-4 space-y-5">
              {results.map((r) => (
                <div key={r.label} className="border-b border-border pb-4 last:border-b-0 last:pb-0">
                  <div className="font-display text-3xl font-semibold text-[var(--gold-deep)]">{r.metric}</div>
                  <div className="mt-1 text-sm text-muted-foreground">{r.label}</div>
                </div>
              ))}
            </div>
            <div className="mt-6">
              {links ?? <CTAButton to="/case-studies" variant="ghost">See more case studies</CTAButton>}
            </div>
          </aside>
        </div>
        {closingContent && <p className="mt-8 text-lg text-foreground leading-relaxed">{closingContent}</p>}
      </Section>
    </div>
  );
}

// Preset case studies keyed by practice-area slug. Anything not listed falls back to the generic builder.
export function practiceCaseStudy(
  slug: string,
  name: string,
  shortName: string,
  results: { metric: string; label: string }[],
): CaseStudyProps {
  const presets: Record<string, Partial<CaseStudyProps>> = {
    "personal-injury-lawyer-seo": {
      title: "How a Houston PI firm 3x'd signed cases in 9 months.",
      firmType: "Personal Injury Firm · 6 attorneys",
      location: "Houston, TX",
      challenge:
        "A Houston PI firm was spending $42k/month on Google Ads with declining case quality and getting outranked for 'car accident lawyer Houston' by national lead-resellers and a 40-year-old competitor.",
      approach: [
        "Rebuilt city + case-type architecture: /car-accident-lawyer-houston, /truck-accident, /motorcycle, /wrongful-death",
        "Optimized 3 Google Business Profiles across Houston metro with weekly posts and review velocity",
        "Earned 34 editorial links from local news + legal publishers over 9 months",
        "Attorney-reviewed content library covering 60+ injury sub-topics with Attorney + FAQ schema",
      ],
      quote:
        "By month seven we cut paid spend by 60% and were still signing more cases. PageOne changed our economics.",
      attribution: "Managing Partner, Houston PI firm",
    },
    "dui-dwi-lawyer-seo": {
      title: "How a DUI defense firm won the 2 AM local pack.",
      firmType: "DUI / Criminal Defense · 3 attorneys",
      location: "Phoenix, AZ",
      challenge:
        "A Phoenix DUI firm was invisible on mobile after 10 PM, the exact window their clients search from a police station. Bail-bond and lead-gen sites owned the top 5 results.",
      approach: [
        "Mobile-first landing pages with click-to-call above the fold and EmergencyService schema",
        "Modifier pages: first-offense, felony, refusal, CDL, extreme-DUI, underage",
        "Reputation program to bury mugshot sites for attorney and firm-name queries",
        "24/7 intake integration + call tracking mapped to organic keyword",
      ],
      quote:
        "After-hours calls quadrupled. Organic is now the majority of our new-client pipeline.",
      attribution: "Founding Attorney, Phoenix DUI firm",
    },
    "family-law-lawyer-seo": {
      title: "How a family law firm doubled retainer consults in 8 months.",
      firmType: "Family Law · 4 attorneys",
      location: "Denver, CO",
      challenge:
        "A Denver divorce and custody firm ranked page 3 for their core terms and was losing consults to a national divorce.com-style aggregator.",
      approach: [
        "Rebuilt topical hub around divorce, custody, alimony, mediation, high-asset divorce",
        "Long-form guides written by senior attorneys, with schema marking author expertise",
        "Local links from Denver bar sections, family therapists, and financial advisors",
        "Consultation-focused CTAs A/B tested to lift form completion 41%",
      ],
      quote: "We finally look bigger online than the aggregators, and consults doubled.",
      attribution: "Partner, Denver family law firm",
    },
  };
  const preset = presets[slug];
  return {
    title: preset?.title ?? `How a ${shortName.toLowerCase()} firm scaled organic case volume with PageOne.`,
    firmType: preset?.firmType ?? `${shortName} Firm`,
    location: preset?.location,
    challenge:
      preset?.challenge ??
      `The firm was competing in a saturated ${shortName.toLowerCase()} market against larger practices with older domains, deeper link profiles, and aggressive paid-search budgets, while their own site struggled to rank for the highest-intent case terms.`,
    approach:
      preset?.approach ?? [
        `Full technical + on-page SEO rebuild for the firm's ${shortName.toLowerCase()} practice`,
        `Attorney-reviewed content strategy mapped to every ${shortName.toLowerCase()} case type`,
        `Google Business Profile optimization and local pack ranking program`,
        `Editorial + legal-directory link acquisition tied to authority signals`,
      ],
    results,
    quote: preset?.quote ?? `PageOne's team understood our practice, our clients, and our economics, and delivered case growth we can measure.`,
    attribution: preset?.attribution ?? `Managing Partner, ${shortName} firm`,
  };
}

export function locationCaseStudy(location: string): CaseStudyProps {
  return {
    title: `How a ${location} law firm went from page 3 to #1 in the local pack.`,
    firmType: "Multi-practice law firm",
    location,
    challenge: `A ${location}-based firm was ranking page 3 for their core practice terms and completely invisible in the ${location} local pack, losing 8–10 qualified consultations per month to competitors with older domains and larger review counts.`,
    approach: [
      `Full local audit of ${location} SERPs, GBP, citations, and competitor link profiles`,
      `${location}-specific city + practice landing pages built around real search intent`,
      `Review acquisition + response engine to close the star-rating and volume gap`,
      `Editorial links from ${location} news, bar association, and community publishers`,
    ],
    results: [
      { metric: "+284%", label: `Organic ${location} traffic in 9 months` },
      { metric: "Top 3", label: `Local pack for primary practice term in ${location}` },
      { metric: "3.4x", label: "Monthly qualified consultations vs. baseline" },
    ],
    quote: `We stopped worrying about paid CPC. Ranking in ${location} organically became our unfair advantage.`,
    attribution: `Managing Partner, ${location} law firm`,
  };
}

export function serviceCaseStudy(serviceName: string): CaseStudyProps {
  return {
    title: serviceName,
    challenge: "",
    approach: [],
    results: [],
    directoryOnly: true,
  };
}